import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { API_SCHEMA_ID } from '../../documentationPage/sdlPage/sdlDisplay/SDLDisplay';

export const UseChangedSubgraphHashesFirstLaunchFragment = gql`
  fragment UseChangedSubgraphHashesFirstLaunchFragment on Launch {
    id
    createdAt
    buildInput {
      ... on CompositionBuildInput {
        subgraphs {
          hash
          name
        }
      }
    }
    build {
      id
      result {
        ... on BuildSuccess {
          coreSchema {
            # TODO: add back supergraph fetching via launch once permission bug is fixed
            coreHash
            apiHash
          }
        }
      }
    }
  }
`;

export function useChangedSubgraphHashes(
  latestLaunch:
    | GraphQLTypes.ProposalBuildInputFragment['latestLaunch']
    | undefined,
  firstLaunch:
    | GraphQLTypes.UseChangedSubgraphHashesFirstLaunchFragment
    | undefined,
  latestApprovedLaunch:
    | GraphQLTypes.SupergraphAndApiSchemaChangesFragment['latestApprovedLaunch']
    | undefined,
  activeSchemaPublish:
    | NonNullable<
        GraphQLTypes.UseRemovedCoordinatesLookupGraphFragment['variant']
      >['activeSchemaPublish']
    | undefined,
) {
  return useMemo(() => {
    const lookup: Record<
      string,
      | { oldHash?: string; newHash: string }
      | { oldHash: string; newHash?: string }
    > = {};
    const buildInput =
      latestLaunch?.buildInput?.__typename === 'CompositionBuildInput'
        ? latestLaunch.buildInput
        : undefined;

    const firstLaunchBuildResult =
      firstLaunch?.build?.result?.__typename === 'BuildSuccess'
        ? firstLaunch.build.result
        : undefined;

    const latestApprovedBuildResult =
      latestApprovedLaunch?.build?.result?.__typename === 'BuildSuccess'
        ? latestApprovedLaunch.build.result
        : undefined;
    // if there are no subgraph configs, use the api schema only
    const sourceApiSchemaHash =
      firstLaunchBuildResult?.coreSchema.apiHash ??
      activeSchemaPublish?.history?.at(-1)?.schema.hash;
    if (
      !isMinLengthArray(1, buildInput?.subgraphs) &&
      (latestApprovedBuildResult?.coreSchema || activeSchemaPublish?.schema) &&
      sourceApiSchemaHash
    )
      lookup[API_SCHEMA_ID] = {
        newHash:
          latestApprovedBuildResult?.coreSchema.apiHash ??
          activeSchemaPublish?.schema?.hash,
        oldHash: sourceApiSchemaHash,
      };

    buildInput?.subgraphs?.forEach((subgraphConfig) => {
      lookup[subgraphConfig.name] = { newHash: subgraphConfig.hash };
    });

    const lastBuildInputs = firstLaunch?.buildInput;
    const lastSubgraphConfigs =
      lastBuildInputs?.__typename === 'CompositionBuildInput'
        ? lastBuildInputs.subgraphs
        : null;

    lastSubgraphConfigs?.forEach((subgraphConfig) => {
      getOrSetRecord(
        subgraphConfig.name,
        { oldHash: subgraphConfig.hash },
        lookup,
      ).oldHash = subgraphConfig.hash;
    });

    return Object.fromEntries(
      Object.entries(lookup).filter(
        ([, { oldHash, newHash }]) => oldHash !== newHash,
      ),
    );
  }, [
    activeSchemaPublish?.history,
    activeSchemaPublish?.schema,
    firstLaunch?.build?.result,
    firstLaunch?.buildInput,
    latestApprovedLaunch?.build?.result,
    latestLaunch?.buildInput,
  ]);
}
